import type { IPortfolioTreeDraftParams } from '@/types/IPortfolioTree';
import type { ComputedRef } from 'vue';
import { computed, unref } from 'vue';
import { usePortfolioViewMode } from './usePortfolioViewMode';
import { useGetLivePortfolioVersionTypeToSet, usePortfolioVersionTypeGlobalState } from './usePortfolioVersionType';
import { useHistoryMode, useConstituentAnalysisDate, useRollingDate } from './useConstituentRiskUtilities';
import type { RouteLocationNormalized } from 'vue-router';
import { useIsPdf } from './usePdf';
import type { PortfolioVersionType } from '@/constants/PortfolioVersionType';
import type { MaybeRef } from '@vueuse/shared';
import usePeriod from './usePeriod';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import { getReferenceCodeOrSlug } from '@/utils/portfolioTree';
import { getDraftParams, getDraftRequestVersionType } from '@/utils/getDraftParams';

/**
 * Builds reactive draft query params from the current route, view mode, and version type.
 */
export const useGetDraftParams = ({
  route,
  force,
}: {
  route: RouteLocationNormalized;
  force?: MaybeRef<Partial<IPortfolioTreeDraftParams> | null>;
}): {
  /**
   * Reactive request body for the portfolio draft query, derived from the current
   * route, version type, and view mode.
   */
  draftParams: ComputedRef<IPortfolioTreeDraftParams>;
} => {
  const { portfolioTreeUnderAnalysis } = useItemUnderAnalysisGlobalState();
  const constituentAnalysisDate = useConstituentAnalysisDate();
  const rollingDate = useRollingDate();
  const { period } = usePeriod();
  const { portfolioViewModeOverride } = usePortfolioViewMode();
  const { versionType, versionTypeOverride } = usePortfolioVersionTypeGlobalState();
  const livePortfolioVersionTypeToSet = useGetLivePortfolioVersionTypeToSet({ route });
  const isRiskHistoryMode = useHistoryMode();
  const isPdf = useIsPdf();

  const versionTypeToUse = computed(
    (): PortfolioVersionType =>
      getDraftRequestVersionType({
        isPdf: isPdf.value,
        routeName: route.name,
        routeQueryVersion: route.query.version as PortfolioVersionType | undefined,
        versionType: versionType.value,
        versionTypeOverride: versionTypeOverride.value,
        livePortfolioVersionTypeToSet: livePortfolioVersionTypeToSet.value,
      }),
  );

  const draftParams = computed((): IPortfolioTreeDraftParams => {
    const doesRouteMatchPortfolioUnderAnalysis =
      portfolioTreeUnderAnalysis.value != null &&
      getReferenceCodeOrSlug(portfolioTreeUnderAnalysis.value) === route.params.indexUniqueIdentifier;

    return getDraftParams({
      versionTypeToUse: versionTypeToUse.value,
      mode: portfolioViewModeOverride.value,
      isPdf: isPdf.value,
      isRiskHistoryMode: isRiskHistoryMode.value,
      rollingDate: rollingDate.value,
      constituentAnalysisDate: constituentAnalysisDate.value,
      periodFromDate: period.value.fromDateString(),
      periodToDate: period.value.toDateString(),
      routeQueryEndDate: route.query.endDate as string | undefined,
      doesRouteMatchPortfolioUnderAnalysis,
      force: unref(force),
    });
  });

  return {
    draftParams,
  };
};
